import { useEffect, useRef } from "react";
import { ArrowUpRight, Clock, Repeat } from "lucide-react";
import { ListingLink } from "./ListingLink";
import { resolveImg, track } from "@/lib/marketplaceTrack";

function useImpression(id) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !id) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { track("view", id); io.disconnect(); }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [id]);
  return ref;
}

export function ListingCard({ listing, categoryName, index = 0 }) {
  const ref = useImpression(listing.id);
  const external = /^https?:\/\//.test(listing.destination_url || "");
  return (
    <article ref={ref} data-testid={`mkt-card-${listing.id}`}
      className="mkt-card group relative flex flex-col overflow-hidden rounded-lg bg-[#121212] border border-white/[0.07] hover:border-[#D4AF37]/50 transition-colors duration-300"
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}>
      <ListingLink listing={listing} testid={`mkt-card-link-${listing.id}`} className="flex flex-col h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]">
        <div className="relative aspect-video overflow-hidden bg-black">
          {listing.image_url && (
            <img src={resolveImg(listing.image_url)} alt={listing.title} loading="lazy"
              className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-[1.04] transition-[transform,opacity] duration-700" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent" />
          <div className="absolute top-3 left-3 flex gap-2">
            {listing.coming_soon && <Badge icon={Clock} tone="amber" testid={`mkt-badge-soon-${listing.id}`}>Coming soon</Badge>}
            {listing.pricing_type === "subscription" && <Badge icon={Repeat} tone="gold">Subscription</Badge>}
          </div>
        </div>
        <div className="flex flex-col flex-1 p-5">
          <div className="text-[10px] tracking-[0.22em] uppercase text-neutral-500">{categoryName || listing.category}</div>
          <h3 className="font-serif-display text-2xl text-white mt-1.5 leading-tight">{listing.title}</h3>
          {listing.description && <p className="text-sm text-neutral-400 mt-2 leading-relaxed line-clamp-2">{listing.description}</p>}
          <div className="mt-auto pt-5 flex items-center justify-between">
            <span className="text-[#D4AF37] text-sm font-medium" data-testid={`mkt-price-${listing.id}`}>{listing.price_label}</span>
            <span className="inline-flex items-center gap-1.5 text-xs tracking-widest uppercase text-neutral-300 group-hover:text-[#D4AF37] transition-colors">
              {listing.cta_label || "Explore"}
              <ArrowUpRight size={14} className={`transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 ${external ? "" : "rotate-45"}`} />
            </span>
          </div>
        </div>
      </ListingLink>
    </article>
  );
}

function Badge({ icon: Icon, tone, children, testid }) {
  const cls = tone === "amber" ? "bg-amber-500/15 text-amber-200 border-amber-400/30" : "bg-black/60 text-[#D4AF37] border-[#D4AF37]/40";
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-1 text-[9px] tracking-widest uppercase rounded border backdrop-blur-md ${cls}`}>
      <Icon size={10} /> {children}
    </span>
  );
}
